import { useState, type ReactNode } from 'react';
import { useSaveCertificate, errMessage, type CertificateRow } from './systemMasters';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { fmtLocalDate } from '../../../utils/datetime';

/** Certificate editor (New / Edit) — 2-column layout matching the LIMS Certificate dialog. */
export function CertificateDetailDialog({ companyId, certificate, onClose, onStage }: {
  companyId: string; certificate: CertificateRow | null; onClose: () => void;
  /** A not-yet-saved division: hand the values back to be created on the division's Save, instead of saving. */
  onStage?: (v: { item: string | null; text: string | null; expirationDate: string | null; comment: string | null }) => void;
}) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const save = useSaveCertificate(companyId);
  const [item, setItem] = useState(certificate?.item ?? '');
  const [text, setText] = useState(certificate?.text ?? '');
  const [expiration, setExpiration] = useState((certificate?.expirationDate ?? '').slice(0, 10));   // YYYY-MM-DD
  const [comment, setComment] = useState(certificate?.comment ?? '');

  const doSave = async () => {
    try {
      const v = { item: item.trim() || null, text: text.trim() || null, expirationDate: expiration || null, comment: comment.trim() || null };
      if (onStage) { onStage(v); onClose(); return; }
      await save.mutateAsync({ id: certificate?.id, ...v });
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      onClose();
    } catch (e) { notify.error(errMessage(e)); }
  };

  const field = (label: string, control: ReactNode) => (
    <div className="acc-field-h">
      <label className="acc-label">{label}</label>
      <div className="acc-fh-control">{control}</div>
    </div>
  );

  return (
    <div className="acc-overlay" style={{ zIndex: 1250 }} onMouseDown={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="acc-dialog acc-dialog-cert acc-dialog--accent" onMouseDown={(e) => e.stopPropagation()}>
        <div className="acc-dialog-hdr"><span>{item ? `${item} - Certificate` : 'Certificate'}</span><i className="ti ti-x acc-dialog-x" onClick={onClose} /></div>
        <div className="acc-dialog-body">
          <div className="cert-grid">
            <div className="cert-col">
              {field('Item', <input className="acc-input" autoFocus value={item} onChange={(e) => setItem(e.target.value)} />)}
              {field('Text', <input className="acc-input" value={text} onChange={(e) => setText(e.target.value)} />)}
              {field('Expiration Date', <input className={`acc-input acc-date${expiration ? '' : ' acc-date-empty'}`} type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} />)}
            </div>
            <div className="cert-col">
              {field('Last Updated Date', <input className="acc-input" value={certificate?.lastUpdatedDate ? fmtLocalDate(certificate.lastUpdatedDate) : ''} disabled readOnly />)}
              {field('Last Updated By', <input className="acc-input" value={certificate?.lastUpdatedBy || 'N/A'} disabled readOnly />)}
              {field('Comment', <input className="acc-input" value={comment} onChange={(e) => setComment(e.target.value)} />)}
            </div>
          </div>
        </div>
        <div className="acc-dialog-ftr">
          <button className="ca-btn ca-btn-solid" onClick={() => void doSave()} disabled={save.isPending}><span className="tb-i">💾</span> OK</button>
          <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        </div>
      </div>
    </div>
  );
}
